import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { sessionStore, setSessionExpiredHandler } from '../api/client';
import { authApi } from '../api/endpoints';
import type { PartnerAccountType, UserProfile } from '../api/types';

interface AuthState {
  user: UserProfile | null;
  /** True until the stored session has been checked, so guards do not flash. */
  initialising: boolean;
  isAuthenticated: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  login: (
    emailOrPhone: string,
    password: string,
    options?: { organizationName?: string; accountType?: PartnerAccountType },
  ) => Promise<UserProfile>;
  register: (body: {
    fullName: string;
    email: string;
    phone: string;
    password: string;
    accountType: 'Customer' | 'Owner';
    businessName?: string;
    categories?: string[];
  }) => Promise<UserProfile>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [initialising, setInitialising] = useState(true);

  const clearSession = useCallback(() => {
    sessionStore.clear();
    setUser(null);
  }, []);

  // When a refresh finally fails, the axios layer calls this so the UI drops back to a
  // signed-out state instead of looping on 401s.
  useEffect(() => {
    setSessionExpiredHandler(clearSession);
  }, [clearSession]);

  useEffect(() => {
    let cancelled = false;

    async function restore() {
      if (!sessionStore.read()) {
        setInitialising(false);
        return;
      }

      try {
        const profile = await authApi.me();
        if (!cancelled) setUser(profile);
      } catch {
        if (!cancelled) clearSession();
      } finally {
        if (!cancelled) setInitialising(false);
      }
    }

    void restore();

    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  const login = useCallback(async (
    emailOrPhone: string,
    password: string,
    options?: { organizationName?: string; accountType?: PartnerAccountType },
  ) => {
    const response = await authApi.login(emailOrPhone, password, options ?? {});

    sessionStore.write({
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      accessTokenExpiresAtUtc: response.accessTokenExpiresAtUtc,
    });

    setUser(response.user);
    return response.user;
  }, []);

  const register = useCallback<AuthState['register']>(async (body) => {
    const response = await authApi.register(body);

    sessionStore.write({
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      accessTokenExpiresAtUtc: response.accessTokenExpiresAtUtc,
    });

    setUser(response.user);
    return response.user;
  }, []);

  const logout = useCallback(async () => {
    const session = sessionStore.read();

    if (session?.refreshToken) {
      // Best effort: the local session is cleared either way.
      try {
        await authApi.logout(session.refreshToken);
      } catch {
        /* ignore */
      }
    }

    clearSession();
  }, [clearSession]);

  const refreshProfile = useCallback(async () => {
    if (!sessionStore.read()) return;

    try {
      setUser(await authApi.me());
    } catch {
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      initialising,
      isAuthenticated: user !== null,
      isOwner: user?.roles.includes('Owner') ?? false,
      isAdmin: user?.roles.includes('SuperAdmin') ?? false,
      login,
      register,
      logout,
      refreshProfile,
    }),
    [user, initialising, login, register, logout, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider.');
  }

  return context;
}
